import React, { useState, useEffect, useRef, useCallback } from 'react';
import { parseVerilog, propagateLogic, parseYosysJson } from './astParser';

// ── Zoom constants ──────────────────────────────────────────────────
const MIN_ZOOM = 0.15;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.15;

const SchematicViewer = ({ 
  theme, 
  problemTitle, 
  code, 
  parsedGraph: parentGraph, 
  logicValues: parentValues, 
  onToggleInput: parentToggleInput 
}) => {
  const [localGraph, setLocalGraph] = useState(null);
  const [localValues, setLocalValues] = useState({});

  // ── Zoom state ────────────────────────────────────────────────────
  const [zoom, setZoom]  = useState(1);
  const containerRef     = useRef(null);
  const canvasSizeRef    = useRef({ w: 680, h: 340 });

  // Self-parse code if parent didn't provide parsedGraph
  useEffect(() => {
    if (!parentGraph && code) {
      try {
        let graph;
        if (code.trim().startsWith('{')) {
          const jsonObj = JSON.parse(code);
          graph = parseYosysJson(jsonObj);
        } else {
          graph = parseVerilog(code);
        }
        
        if (graph) {
          setLocalGraph(graph);
          setLocalValues(prev => {
            const next = {};
            graph.inputs.forEach(inp => {
              next[inp] = prev[inp] !== undefined ? prev[inp] : 0;
            });
            return propagateLogic(graph, next);
          });
        }
      } catch (err) {
        console.error("Schematic auto-parse error:", err);
      }
    }
  }, [code, parentGraph]);

  const activeGraph = parentGraph || localGraph || { inputs: [], outputs: [], wires: [], gates: [] };
  const activeValues = parentValues || localValues;

  const handleToggleLocalInput = (port) => {
    if (parentToggleInput) {
      parentToggleInput(port);
    } else {
      setLocalValues(prev => {
        const next = { ...prev, [port]: prev[port] ? 0 : 1 };
        return propagateLogic(activeGraph, next);
      });
    }
  };

  // Wire styling based on logic state
  const wireColor = (val) => (val ? '#10b981' : '#475569');
  const wireWidth = (val) => (val ? 2.5 : 1.5);
  
  const isLight = theme === 'light';
  const svgBg   = isLight ? '#f1f5f9' : '#05080e';
  const gateFill = isLight ? '#e2e8f0' : '#0f172a';
  const portFill = isLight ? '#cbd5e1' : '#0f172a';
  const portText = isLight ? '#0f172a' : '#ffffff';

  const inputsList  = activeGraph.inputs  || [];
  const outputsList = activeGraph.outputs || [];
  const gatesList   = activeGraph.gates   || [];

  // ── Zoom helpers ──────────────────────────────────────────────────
  const clampZoom = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

  const handleZoomIn  = () => setZoom(z => clampZoom(+(z + ZOOM_STEP).toFixed(2)));
  const handleZoomOut = () => setZoom(z => clampZoom(+(z - ZOOM_STEP).toFixed(2)));

  const handleFit = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const { w, h } = canvasSizeRef.current;
    const cw = container.clientWidth  - 32;
    const ch = container.clientHeight - 32;
    const scale = clampZoom(Math.min(cw / w, ch / h));
    setZoom(scale);
  }, []);

  const handleReset = () => { setZoom(1); };

  // ── Scroll-wheel zoom ─────────────────────────────────────────────
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
    setZoom(z => clampZoom(+(z + delta).toFixed(2)));
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);


  if (inputsList.length === 0 && outputsList.length === 0 && gatesList.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%', 
        backgroundColor: svgBg, 
        color: portText, 
        padding: '20px', 
        alignItems: 'center', 
        justifyContent: 'center', 
        textAlign: 'center'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '24px' }}>🔌</span>
          <strong style={{ fontSize: '13px', color: portText }}>Logic Schematic Not Generated</strong>
          <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#64748b', maxWidth: '320px' }}>
            Interactive logic schematic will be generated dynamically upon compiling or writing your logic gates in Verilog.
          </p>
        </div>
      </div>
    );
  }

  // Topologically assign levels to determine X coordinates
  const netLevels = {};
  inputsList.forEach(inp => { netLevels[inp] = 0; });

  const unresolvedGates = [...gatesList];
  const gateLevels = {};
  let resolvedAny = true;
  let iterations = 0;

  while (unresolvedGates.length > 0 && resolvedAny && iterations < 100) {
    resolvedAny = false;
    iterations++;
    for (let i = unresolvedGates.length - 1; i >= 0; i--) {
      const gate = unresolvedGates[i];
      const canResolve = gate.inputs.every(inp => netLevels[inp] !== undefined);
      if (canResolve) {
        const maxLevel = gate.inputs.reduce((max, inp) => Math.max(max, netLevels[inp]), 0);
        const level = maxLevel + 1;
        gateLevels[gate.id] = level;
        gate.outputs.forEach(out => { netLevels[out] = level; });
        unresolvedGates.splice(i, 1);
        resolvedAny = true;
      }
    }
  }

  // Default level 1 for any cycles or dangling nets
  unresolvedGates.forEach(gate => {
    gateLevels[gate.id] = 1;
    gate.outputs.forEach(out => { netLevels[out] = 1; });
  });

  // Spacing constants
  const H_SPACE = 160;
  const V_SPACE = 65;
  
  const inputCoords  = {};
  const outputCoords = {};
  const gateCoords   = {};

  // Assign coordinates to Inputs
  inputsList.forEach((inp, idx) => {
    inputCoords[inp] = { x: 50, y: 60 + idx * V_SPACE };
  });

  // Group and assign coordinates to Gates
  const levelGroups = {};
  gatesList.forEach(gate => {
    const lvl = gateLevels[gate.id] || 1;
    if (!levelGroups[lvl]) levelGroups[lvl] = [];
    levelGroups[lvl].push(gate);
  });

  let maxLevel = 0;
  Object.keys(levelGroups).forEach(lvl => {
    const lvlNum = parseInt(lvl);
    if (lvlNum > maxLevel) maxLevel = lvlNum;
    const gatesInLvl = levelGroups[lvl];
    gatesInLvl.forEach((gate, idx) => {
      gateCoords[gate.id] = {
        x: 80 + lvlNum * H_SPACE,
        y: 60 + idx * (V_SPACE + 20)
      };
    });
  });

  // Assign coordinates to Outputs
  outputsList.forEach((out, idx) => {
    outputCoords[out] = {
      x: 120 + (maxLevel + 1) * H_SPACE,
      y: 60 + idx * V_SPACE
    };
  });

  // Calculate dynamic size of canvas
  const canvasWidth  = Math.max(680, 180 + (maxLevel + 1) * H_SPACE);
  const canvasHeight = Math.max(340, Math.max(inputsList.length, outputsList.length, gatesList.length) * V_SPACE + 60);

  // Keep canvas size accessible for fit handler
  canvasSizeRef.current = { w: canvasWidth, h: canvasHeight };

  // Helper to trace connection coordinates
  const getNetCoordinates = (netName) => {
    if (inputCoords[netName]) return inputCoords[netName];
    // Find gate that outputs this net
    const producer = gatesList.find(g => g.outputs.includes(netName));
    if (producer && gateCoords[producer.id]) {
      // Connect to output of this gate (offset to the right of gate bounds)
      return { 
        x: gateCoords[producer.id].x + 45, 
        y: gateCoords[producer.id].y + 30 
      };
    }
    return { x: 50, y: 50 };
  };

  // ── Shared button styles ──────────────────────────────────────────
  const btnBase = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: 'none', borderRadius: '6px', cursor: 'pointer',
    fontWeight: 'bold', userSelect: 'none', lineHeight: 1,
    transition: 'opacity 0.15s',
  };
  const iconBtnStyle = {
    ...btnBase,
    width: '28px', height: '28px', fontSize: '17px',
    backgroundColor: isLight ? '#e2e8f0' : '#1e293b',
    color: isLight ? '#0f172a' : '#e2e8f0',
    border: `1px solid ${isLight ? '#cbd5e1' : '#334155'}`,
  };
  const textBtnStyle = {
    ...btnBase,
    padding: '0 10px', height: '28px', fontSize: '11px', gap: '4px',
    backgroundColor: isLight ? '#e2e8f0' : '#1e293b',
    color: isLight ? '#0f172a' : '#e2e8f0',
    border: `1px solid ${isLight ? '#cbd5e1' : '#334155'}`,
  };
  const zoomBadgeStyle = {
    minWidth: '44px', textAlign: 'center', fontSize: '11px',
    fontWeight: 'bold', cursor: 'default', userSelect: 'none',
    color: isLight ? '#0f172a' : '#e2e8f0',
    backgroundColor: isLight ? '#f1f5f9' : '#0f172a',
    border: `1px solid ${isLight ? '#cbd5e1' : '#334155'}`,
    borderRadius: '6px', padding: '4px 6px',
  };

  const zoomPercent = Math.round(zoom * 100);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: 'var(--console-bg)', color: 'var(--text-main)' }}>

      {/* ── Toolbar ── */}
      <div style={{
        padding: '6px 12px',
        borderBottom: '1px solid var(--border-color)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        backgroundColor: 'var(--header-bg)', fontSize: '11px',
        gap: '8px', flexWrap: 'wrap',
      }}>
        {/* Left: schematic title */}
        <span style={{ color: '#64748b', flexShrink: 0 }}>
          <strong>Dynamic Netlist Schematic</strong> {problemTitle ? `(${problemTitle})` : ''}
        </span>

        {/* Centre: hint */}
        <span style={{ color: '#10b981', fontWeight: 'bold', fontSize: '10px', textAlign: 'center', flexShrink: 1 }}>
          💡 Click inputs to toggle · Scroll to zoom
        </span>

        {/* Right: zoom controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
          <button title="Zoom Out (scroll ↓)" style={iconBtnStyle} onClick={handleZoomOut}>−</button>

          <div style={zoomBadgeStyle}>{zoomPercent}%</div>

          <button title="Zoom In (scroll ↑)" style={iconBtnStyle} onClick={handleZoomIn}>+</button>

          <button title="Fit schematic to window" style={textBtnStyle} onClick={handleFit}>
            {/* expand icon */}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/>
              <line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
            </svg>
            Fit
          </button>

          <button title="Reset zoom to 100%" style={textBtnStyle} onClick={handleReset}>
            {/* reset icon */}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="1 4 1 10 7 10"/>
              <path d="M3.51 15a9 9 0 1 0 .49-5.38L1 10"/>
            </svg>
            Reset
          </button>
        </div>
      </div>

      {/* ── Main SVG Render area ── */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          overflow: 'auto',
          position: 'relative',
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg
          width={canvasWidth}
          height={canvasHeight}
          style={{
            background: svgBg,
            borderRadius: '8px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
            transform: `scale(${zoom})`,
            transformOrigin: 'center center',
            transition: 'transform 0.06s ease',
            display: 'block',
            flexShrink: 0,
          }}
        >
          <defs>
            <marker id="arrow" viewBox="0 0 10 10" refX="6" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
              <path d="M 0 2 L 8 5 L 0 8 z" fill="#475569" />
            </marker>
          </defs>

          {/* ================= WIRES (Connections) ================= */}
          {gatesList.map(gate => {
            const gatePos = gateCoords[gate.id];
            if (!gatePos) return null;

            return gate.inputs.map((inp, inpIdx) => {
              const src  = getNetCoordinates(inp);
              const destX = gatePos.x;
              // Distribute input pins vertically on the left of the gate boundary
              const destY = gatePos.y + (gate.inputs.length === 1 ? 30 : (inpIdx === 0 ? 15 : 45));
              const val  = activeValues[inp] || 0;

              // Orthogonal S-curve wire routing path
              const midX = src.x + (destX - src.x) / 2;
              const pathD = `M ${src.x},${src.y} L ${midX},${src.y} L ${midX},${destY} L ${destX},${destY}`;

              return (
                <path
                  key={`${gate.id}-in-${inpIdx}`}
                  d={pathD}
                  stroke={wireColor(val)}
                  strokeWidth={wireWidth(val)}
                  fill="none"
                />
              );
            });
          })}

          {/* Output Ports Wires */}
          {outputsList.map((out, idx) => {
            const src  = getNetCoordinates(out);
            const dest = outputCoords[out];
            if (!src || !dest) return null;
            const val  = activeValues[out] || 0;

            const midX = src.x + (dest.x - src.x) / 2;
            const pathD = `M ${src.x},${src.y} L ${midX},${src.y} L ${midX},${dest.y} L ${dest.x},${dest.y}`;

            return (
              <path
                key={`out-wire-${idx}`}
                d={pathD}
                stroke={wireColor(val)}
                strokeWidth={wireWidth(val)}
                fill="none"
                markerEnd="url(#arrow)"
              />
            );
          })}

          {/* ================= GATES ================= */}
          {gatesList.map(gate => {
            const pos = gateCoords[gate.id];
            if (!pos) return null;

            const isXOR  = gate.type.includes('XOR');
            const isAND  = gate.type.includes('AND');
            const isOR   = gate.type.includes('OR');
            const isNOT  = gate.type.includes('NOT');
            const isNAND = gate.type.includes('NAND');
            const isNOR  = gate.type.includes('NOR');
            const isXNOR = gate.type.includes('XNOR');

            let strokeColor = '#3b82f6';
            let label = 'GATE';
            let gatePath = "M 0,0 L 40,0 L 40,60 L 0,60 Z"; // default box

            if (isAND) {
              strokeColor = '#3b82f6';
              label = 'AND';
              gatePath = "M 0,0 L 22,0 C 42,0 42,60 22,60 L 0,60 Z";
            } else if (isNAND) {
              strokeColor = '#3b82f6';
              label = 'NAND';
              gatePath = "M 0,0 L 20,0 C 38,0 38,60 20,60 L 0,60 Z";
            } else if (isXOR) {
              strokeColor = '#8b5cf6';
              label = 'XOR';
              gatePath = "M 0,0 C 15,10 15,50 0,60 C 10,60 26,55 40,30 C 26,5 10,0 0,0 Z M -4,0 C 10,10 10,50 -4,60";
            } else if (isXNOR) {
              strokeColor = '#8b5cf6';
              label = 'XNOR';
              gatePath = "M 0,0 C 15,10 15,50 0,60 C 10,60 24,55 36,30 C 24,5 10,0 0,0 Z M -4,0 C 10,10 10,50 -4,60";
            } else if (isOR) {
              strokeColor = '#ef4444';
              label = 'OR';
              gatePath = "M 0,0 C 10,10 10,50 0,60 C 15,60 30,50 40,30 C 30,10 15,0 0,0 Z";
            } else if (isNOR) {
              strokeColor = '#ef4444';
              label = 'NOR';
              gatePath = "M 0,0 C 10,10 10,50 0,60 C 15,60 28,50 36,30 C 28,10 15,0 0,0 Z";
            } else if (isNOT) {
              strokeColor = '#f59e0b';
              label = 'NOT';
              gatePath = "M 0,10 L 30,30 L 0,50 Z";
            } else {
              strokeColor = '#64748b';
              label = 'BUF';
              gatePath = "M 0,10 L 32,30 L 0,50 Z";
            }

            const gateOutVal = activeValues[gate.outputs[0]] || 0;

            return (
              <g key={gate.id} transform={`translate(${pos.x}, ${pos.y})`}>
                <path d={gatePath} fill={gateFill} stroke={strokeColor} strokeWidth="2" />
                
                {/* Bubble details for inverted gates */}
                {(isNAND || isNOR || isXNOR) && (
                  <circle cx="42" cy="30" r="3.5" fill={gateFill} stroke={strokeColor} strokeWidth="2" />
                )}
                {isNOT && (
                  <circle cx="34" cy="30" r="3" fill={gateFill} stroke={strokeColor} strokeWidth="1.5" />
                )}

                <text x="14" y="34" fill={strokeColor} fontSize="8" fontWeight="bold">{label}</text>
                <text x="48" y="24" fill={wireColor(gateOutVal)} fontSize="8" fontWeight="bold">
                  {gate.outputs[0]} ({gateOutVal})
                </text>
              </g>
            );
          })}

          {/* ================= PORTS ================= */}
          {/* Input ports */}
          {inputsList.map((inp) => {
            const pos = inputCoords[inp];
            const val = activeValues[inp] || 0;
            return (
              <g 
                key={`inp-port-${inp}`} 
                transform={`translate(${pos.x - 30}, ${pos.y - 12})`} 
                style={{ cursor: 'pointer' }}
                onClick={(e) => { e.stopPropagation(); handleToggleLocalInput(inp); }}
              >
                <rect width="45" height="24" rx="4" fill={val ? '#10b981' : '#1e293b'} stroke="#64748b" />
                <text x="22.5" y="15" textAnchor="middle" fill="#fff" fontSize="10" fontWeight="bold">
                  {inp}={val}
                </text>
              </g>
            );
          })}

          {/* Output ports */}
          {outputsList.map((out) => {
            const pos = outputCoords[out];
            const val = activeValues[out] || 0;
            return (
              <g key={`out-port-${out}`} transform={`translate(${pos.x}, ${pos.y - 12})`}>
                <rect width="50" height="24" rx="4" fill={portFill} stroke={wireColor(val)} strokeWidth="2" />
                <text x="25" y="15" textAnchor="middle" fill={portText} fontSize="9" fontWeight="bold">
                  {out}={val}
                </text>
              </g>
            );
          })}
        </svg>

        {/* ── Scroll hint overlay ── */}
        <div style={{
          position: 'absolute', bottom: '20px', right: '20px',
          fontSize: '9px', color: '#475569',
          pointerEvents: 'none', userSelect: 'none',
        }}>
          Scroll to zoom
        </div>
      </div>
    </div>
  );
};

export default SchematicViewer;
